import React, { useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import { getEventName } from '@op/pages/Overview/KeyMetrics/utils';
import { getOpinionLineChartEventTypeStyle } from '../utils/eventTypeStyle';
import OpinionEventFloater, {
  type IOpinionEventFloaterDetail,
} from '../OpinionEventFloater';
import { buildFloaterDetailFromEvents } from '../hooks/utils/buildFloaterDetailFromEvents';
import { getLocale, useIntl } from 'umi';
import { comparableTime } from '@op/utils/time';
import { ParamsTimeType } from '@op/types/common';
import { resolveOpinionChartXAxisValue } from '../hooks/utils/resolveOpinionChartXAxisValue';
import { useOpinionEventItemPosition } from '../hooks/useOpinionEventItemPosition';
import { useOpinionChartEventItemHover } from '../hooks/useOpinionChartEventItemHover';
import { useOpinionEventFloaterPosition } from '../hooks/useOpinionEventFloaterPosition';
import type { IEventItem, AnchorMode } from '@op/components/ChartsEvents/types';
import './index.scss';

/** 默认最多展示 2 条；超过 2 条时展示第 1 条，第二行展示 +N */
const BUBBLE_MAX_VISIBLE_COUNT = 2;

export default function EventItem({
  myChart,
  xAxisValue,
  eventList,
  hide = true,
  format,
  backPositionInfo,
  halfBarWidth,
  maxTimeValue,
  showEventAxis,
  dateType,
  xAxisData,
  yAxisMin,
  hasAnySelected = false,
  anchorMode = 'start',
  endTime,
  chartRenderEventDetail,
}: {
  myChart: any;
  xAxisValue: string;
  eventList: Array<IEventItem>;
  hide?: boolean;
  format: string;
  halfBarWidth: number;
  maxTimeValue: string;
  showEventAxis: boolean;
  dateType: ParamsTimeType;
  xAxisData: Array<string>;
  yAxisMin?: boolean;
  hasAnySelected?: boolean;
  anchorMode?: AnchorMode;
  endTime?: string;
  chartRenderEventDetail?: IOpinionEventFloaterDetail | null;
  backPositionInfo: (item: {
    eventLine: any;
    barLeftPosition: number;
    barBottomPosition: number;
    eventBox: any;
    anchorMode?: AnchorMode;
    shouldHide?: boolean;
  }) => void;
}) {
  const lang = getLocale();
  const { formatMessage } = useIntl();

  const {
    eventBoxRef,
    eventLineRef,
    currentItemPosition,
    renderMarkArea,
    eventLineShow,
    markAreaShow,
    markAreaTimes,
    setRenderMarkArea,
    setEventLineShow,
    setMarkAreaShow,
    setMarkAreaTimes,
    getBarLeftPosition,
  } = useOpinionEventItemPosition({
    myChart,
    xAxisValue,
    format,
    dateType,
    xAxisData,
    backPositionInfo,
    yAxisMin,
    anchorMode,
  });

  const resetMarkArea = (): void => {
    setMarkAreaShow(false);
    setEventLineShow(false);
    setMarkAreaTimes('');
    setRenderMarkArea({ left: '0', width: '0' });
  };

  const bubbleEventDetail = useMemo(
    () => buildFloaterDetailFromEvents(eventList),
    [eventList],
  );

  const {
    showEventFloater,
    handleEventBubbleEnter,
    handleEventBubbleLeave,
    handleEventLineEnter,
    handleEventLineLeave,
    floaterPointerHandlers,
  } = useOpinionChartEventItemHover({
    myChart,
    xAxisData,
    showEventAxis,
    xAxisValue,
    bubbleEventDetail,
    eventBoxRef,
    eventLineRef,
    setEventLineShow,
    onLeaveMarkArea: resetMarkArea,
  });

  const handleMouseEnter = (item: IEventItem) => {
    if (!item.start_time && !item.end_time) {
      return;
    }

    const resolveAxisTime = (time?: string) => {
      if (!time) {
        return time;
      }
      const { realValue } = resolveOpinionChartXAxisValue(
        time,
        dateType,
        format,
        xAxisData,
      );
      return realValue || time;
    };

    const startTime = resolveAxisTime(moment(item.start_time).format(format));
    const useEndTime = item.end_time || endTime;
    const endTimeFormatted = useEndTime
      ? resolveAxisTime(moment(useEndTime).format(format))
      : startTime;

    let startLeft: number;
    let endLeftValue: number;

    if (anchorMode === 'end') {
      endLeftValue = currentItemPosition.current + halfBarWidth;
      const startTimePosition = getBarLeftPosition({ xAxis: startTime });
      startLeft = startTimePosition.left - halfBarWidth;
    } else {
      startLeft = currentItemPosition.current - halfBarWidth;

      if (useEndTime) {
        const maxThanMaxTime = comparableTime(maxTimeValue, endTimeFormatted);
        const realXAxis = maxThanMaxTime ? maxTimeValue : endTimeFormatted;
        const endItem = getBarLeftPosition({ xAxis: realXAxis });
        endLeftValue = endItem.left + halfBarWidth;
      } else {
        endLeftValue = startLeft + halfBarWidth * 2;
      }
    }

    const firstAxisValue = xAxisData[0];
    const lastAxisValue = maxTimeValue || xAxisData[xAxisData.length - 1];
    if (firstAxisValue && lastAxisValue) {
      const chartStartLeft =
        getBarLeftPosition({ xAxis: firstAxisValue }).left - halfBarWidth;
      const chartEndLeft =
        getBarLeftPosition({ xAxis: lastAxisValue }).left + halfBarWidth;
      startLeft = Math.max(chartStartLeft, startLeft);
      endLeftValue = Math.min(chartEndLeft, endLeftValue);
      if (endLeftValue <= startLeft) {
        endLeftValue = startLeft + halfBarWidth * 2;
      }
    }

    setMarkAreaTimes(anchorMode === 'end' && useEndTime ? endTimeFormatted : startTime);
    setRenderMarkArea({
      left: `${startLeft}px`,
      width: `${endLeftValue - startLeft}px`,
      height: eventLineRef.current?.offsetHeight,
      top: eventLineRef.current?.offsetTop + 4,
    });
    setMarkAreaShow(true);
  };

  const getFloaterAnchor = useCallback(() => {
    if (!eventBoxRef.current) {
      return null;
    }
    const rect = eventBoxRef.current.getBoundingClientRect();
    return {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    };
  }, [eventBoxRef]);

  const floaterStyle = useOpinionEventFloaterPosition(
    showEventFloater,
    getFloaterAnchor,
    'below',
  );

  const totalEvents = eventList.length;
  let visibleEvents = eventList;
  let overflowCount = 0;

  if (totalEvents > BUBBLE_MAX_VISIBLE_COUNT) {
    visibleEvents = eventList.slice(0, 1);
    overflowCount = totalEvents - 1;
  }
  const markAreaWidth = parseFloat(renderMarkArea.width) || 0;

  return (
    <>
      <div
        className={`dbo-mark-area-box ${!showEventAxis ? '--long' : ''} ${
          markAreaShow ? '--show' : '--hide'
        } ${anchorMode === 'end' ? '--anchor-end' : ''}`}
        style={renderMarkArea}
      >
        <div
          className="dbo-mark-area-time"
          style={{
            left: anchorMode === 'start' ? `${halfBarWidth}px` : `${markAreaWidth - halfBarWidth}px`,
          }}
        >
          <p className="dbo-mark-time-item">{markAreaTimes}</p>
        </div>
      </div>
      <div
        ref={eventBoxRef}
        className={`dbo-event-item-wrapper ${hide ? '--hide' : ''} ${hasAnySelected ? '--dimmed' : ''}`}
        onMouseEnter={handleEventBubbleEnter}
        onMouseLeave={handleEventBubbleLeave}
      >
        <ul className="dbo-event-box">
          {visibleEvents.map(item => (
            <li
              className="dbi-opinion-event-item"
              key={item.name}
              style={
                {
                  '--dbi-opinion-event-bar-color':
                    getOpinionLineChartEventTypeStyle(item.event_type).textColor,
                } as React.CSSProperties
              }
              onMouseEnter={() => handleMouseEnter(item)}
            >
              <span className="dbi-opinion-event-item__bar" />
              <span className="dbi-opinion-event-item__text">
                {getEventName(item, lang)}
              </span>
            </li>
          ))}
          {overflowCount > 0 && (
            <li className="dbi-opinion-event-item dbi-opinion-event-item--more">
              +{overflowCount} {formatMessage({ id: 'Events-1' })}
            </li>
          )}
        </ul>
      </div>
      {showEventFloater &&
        floaterStyle &&
        createPortal(
          <OpinionEventFloater
            time={xAxisValue}
            eventDetail={bubbleEventDetail}
            navigationEventDetail={chartRenderEventDetail}
            dateType={dateType}
            style={floaterStyle}
            {...floaterPointerHandlers}
          />,
          document.body,
        )}
      <div
        ref={eventLineRef}
        className={`dbo-event-box-line ${eventLineShow ? '--highlight' : ''}`}
        onMouseEnter={handleEventLineEnter}
        onMouseLeave={handleEventLineLeave}
      ></div>
    </>
  );
}
